import type { DiscountDTO, StorefrontOrderLine } from 'ecosystem'
import {
  allocateQuantityDiscount,
  createMinorUnit,
  type QuantityDiscountBlock,
  sumQuantityDiscountBlocks
} from 'shared-utils'

/**
 * The single funnel every checkout surface prices a line through — the five
 * payment providers, the storefront order builder and the invoice flow all read
 * the same figures out of here, which is what keeps them agreeing to the öre.
 *
 * Two reductions stack, in this order:
 *
 * 1. **The cart discount code**, which may also swap the basis to
 *    `originalPrice` (a non-campaign code discounts the pre-campaign price).
 * 2. **The product's quantity ladder**, applied on top of whatever price step 1
 *    left — matching the platform rule that the ladder discounts whatever unit
 *    price the surface already resolved, never the public chain behind it.
 *
 * Both land in `totalDiscountAmount`, so `totalAmount` keeps its
 * `quantity * unitPrice - totalDiscountAmount` identity and every existing
 * consumer inherits volume pricing without a change: Klarna and Svea carry it
 * on the row (`total_discount_amount` / `DiscountAmount`), Briqpay as
 * `unitDiscountAmountIncVat`, and Nexi and Qliro as the sibling discount line
 * they already emit. None of them needs a blended unit price, so the shopper
 * sees the reduction in the provider's own UI.
 *
 * `blocks` carries the allocation for the surfaces that want the split
 * itemised; `quantityDiscountAmount` is the ladder's share on its own.
 */
export const orderLineValueCalculator = (
  orderLine: StorefrontOrderLine,
  cartDiscountCode: DiscountDTO | null
) => {
  const isDiscountCandidate =
    (orderLine.product.brand?.id &&
      cartDiscountCode?.brandIds?.includes(orderLine.product.brand.id)) ||
    cartDiscountCode?.categoryIds?.some((c) => orderLine.product.categoryIds?.includes(c)) ||
    cartDiscountCode?.tagIds?.some((t) =>
      orderLine.product.tags?.map((ptag) => ptag.id).includes(t)
    )

  const isOriginalPriceLower = orderLine.unitPrice > orderLine.originalPrice

  const quantity = orderLine.quantity
  let unitPrice = createMinorUnit(orderLine.unitPrice)
  let unitPriceWithDiscount = unitPrice
  let codeDiscountAmount = 0

  if (cartDiscountCode && isDiscountCandidate) {
    const discountRate = cartDiscountCode.value / 100

    if (!cartDiscountCode.campaign && !isOriginalPriceLower) {
      unitPrice = createMinorUnit(orderLine.originalPrice)
    }

    codeDiscountAmount = unitPrice * quantity * discountRate
    unitPriceWithDiscount = unitPrice - unitPrice * discountRate
  }

  // Minor units throughout, so the ladder's half-up-to-2-decimals rounding is a
  // round to a whole minor unit. `createMinorUnit` is a bare `* 100` and does
  // not round, so the allocator is the first place the öre is fixed.
  const blocks: QuantityDiscountBlock[] = allocateQuantityDiscount(
    quantity,
    unitPriceWithDiscount,
    orderLine.product.quantityDiscount,
    { minorUnits: true }
  )
  const quantityDiscountAmount = Math.max(
    unitPriceWithDiscount * quantity - sumQuantityDiscountBlocks(blocks),
    0
  )

  const totalDiscountAmount = codeDiscountAmount + quantityDiscountAmount

  return {
    quantity,
    unitPrice,
    unitPriceWithDiscount,
    totalDiscountAmount,
    /** The cart code's share of `totalDiscountAmount`. */
    codeDiscountAmount,
    /** The volume ladder's share of `totalDiscountAmount`. */
    quantityDiscountAmount,
    /** Per-run split of the line — one entry under THRESHOLD, several under PACK. */
    blocks,
    totalAmount: quantity * unitPrice - totalDiscountAmount
  }
}
